<template>
  <AdminLayout>
    <div class="max-w-6xl mx-auto">
      <!-- 헤더 -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-slate-800">상품 관리</h1>
            <p class="text-sm text-slate-500">포인트로 교환할 수 있는 상품을 관리합니다</p>
          </div>
        </div>
        <button
          @click="openCreateModal"
          class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          상품 등록
        </button>
      </div>

      <!-- 카테고리 필터 -->
      <div class="flex gap-2 mb-4 overflow-x-auto pb-2">
        <button
          v-for="cat in categories"
          :key="cat.id"
          @click="selectedCategory = cat.id"
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition',
            selectedCategory === cat.id
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
        >
          {{ cat.icon }} {{ cat.label }}
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-slate-500">상품 불러오는 중...</p>
      </div>

      <!-- 빈 상태 -->
      <div v-else-if="filteredProducts.length === 0" class="bg-white rounded-2xl p-12 text-center shadow-sm">
        <div class="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-slate-600 mb-2">등록된 상품이 없습니다</h3>
        <p class="text-slate-400 mb-4">첫 번째 상품을 등록해보세요.</p>
        <button
          @click="openCreateModal"
          class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          상품 등록
        </button>
      </div>

      <!-- 상품 목록 -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="product in filteredProducts"
          :key="product.id"
          class="bg-white rounded-2xl overflow-hidden shadow-sm"
        >
          <!-- 상품 이미지 -->
          <div class="relative aspect-square bg-slate-100">
            <img
              v-if="product.imageUrl"
              :src="product.imageUrl"
              :alt="product.name"
              class="w-full h-full object-cover"
            />
            <div v-else class="w-full h-full flex items-center justify-center">
              <span class="text-5xl">🎁</span>
            </div>
            <!-- 상태 배지 -->
            <div class="absolute top-3 left-3 flex gap-2">
              <span :class="[
                'px-2 py-1 text-xs font-semibold rounded-full',
                product.isActive ? 'bg-green-500 text-white' : 'bg-slate-500 text-white'
              ]">
                {{ product.isActive ? '판매중' : '판매중지' }}
              </span>
              <span v-if="product.stock === 0" class="px-2 py-1 text-xs font-semibold bg-red-500 text-white rounded-full">
                품절
              </span>
            </div>
          </div>

          <!-- 상품 정보 -->
          <div class="p-4">
            <div class="flex items-start justify-between mb-2">
              <div>
                <p class="text-xs text-slate-400 mb-1">{{ product.brand }}</p>
                <h3 class="font-semibold text-slate-800">{{ product.name }}</h3>
              </div>
              <span class="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded-full">
                {{ getCategoryLabel(product.category) }}
              </span>
            </div>

            <div class="flex items-center justify-between mt-3">
              <div class="text-lg font-bold text-emerald-600">
                {{ product.price.toLocaleString() }}P
              </div>
              <div class="text-sm text-slate-400">
                재고: {{ product.stock < 0 ? '무제한' : product.stock }}
              </div>
            </div>

            <!-- 액션 버튼 -->
            <div class="flex gap-2 mt-4 pt-4 border-t border-slate-100">
              <button
                @click="openEditModal(product)"
                class="flex-1 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition"
              >
                수정
              </button>
              <button
                @click="toggleActive(product)"
                :class="[
                  'flex-1 py-2 text-sm rounded-lg transition',
                  product.isActive
                    ? 'text-amber-600 hover:bg-amber-50'
                    : 'text-green-600 hover:bg-green-50'
                ]"
              >
                {{ product.isActive ? '판매중지' : '판매시작' }}
              </button>
              <button
                @click="deleteProduct(product)"
                class="py-2 px-3 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 상품 등록/수정 모달 -->
      <Teleport to="body">
        <Transition name="modal">
          <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div class="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" @click.stop>
              <h3 class="text-lg font-semibold text-slate-800 mb-4">
                {{ editingProduct ? '상품 수정' : '상품 등록' }}
              </h3>

              <form @submit.prevent="saveProduct" class="space-y-4">
                <!-- 이미지 업로드 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">상품 이미지</label>
                  <div
                    @click="$refs.fileInput.click()"
                    @dragover.prevent
                    @drop.prevent="handleDrop"
                    class="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center cursor-pointer hover:border-emerald-500 transition"
                  >
                    <div v-if="previewUrl" class="relative">
                      <img :src="previewUrl" class="w-full h-40 object-contain rounded-lg bg-slate-50"/>
                      <button
                        type="button"
                        @click.stop="clearImage"
                        class="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        x
                      </button>
                    </div>
                    <div v-else class="py-6">
                      <svg class="w-12 h-12 mx-auto text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      <p class="text-sm text-slate-500">클릭하거나 이미지를 드래그하세요</p>
                    </div>
                  </div>
                  <input
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleFileSelect"
                  />
                </div>

                <!-- 카테고리 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">카테고리 *</label>
                  <select
                    v-model="form.category"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  >
                    <option value="">선택하세요</option>
                    <option v-for="cat in categories.slice(1)" :key="cat.id" :value="cat.id">
                      {{ cat.icon }} {{ cat.label }}
                    </option>
                  </select>
                </div>

                <!-- 브랜드 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">브랜드</label>
                  <input
                    v-model="form.brand"
                    type="text"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="스타벅스, 교촌치킨 등"
                  />
                </div>

                <!-- 상품명 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">상품명 *</label>
                  <input
                    v-model="form.name"
                    type="text"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="아메리카노 Tall"
                    required
                  />
                </div>

                <!-- 설명 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">상품 설명</label>
                  <textarea
                    v-model="form.description"
                    rows="2"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="상품에 대한 간단한 설명"
                  ></textarea>
                </div>

                <!-- 가격 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">가격 (포인트) *</label>
                  <div class="flex items-center gap-2">
                    <input
                      v-model.number="form.price"
                      type="number"
                      min="0"
                      class="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="4500"
                      required
                    />
                    <span class="text-slate-500 font-medium">P</span>
                  </div>
                </div>

                <!-- 재고 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">재고 수량</label>
                  <div class="flex items-center gap-3">
                    <input
                      v-model.number="form.stock"
                      type="number"
                      min="-1"
                      class="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="10"
                      :disabled="form.unlimitedStock"
                    />
                    <label class="flex items-center gap-2 text-sm text-slate-600 whitespace-nowrap">
                      <input
                        v-model="form.unlimitedStock"
                        type="checkbox"
                        class="w-4 h-4 text-emerald-600 rounded"
                      />
                      무제한
                    </label>
                  </div>
                </div>

                <!-- 정렬 순서 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">노출 순서</label>
                  <input
                    v-model.number="form.order"
                    type="number"
                    min="0"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="0 (낮을수록 먼저 표시)"
                  />
                </div>

                <!-- 버튼 -->
                <div class="flex gap-2 pt-4">
                  <button
                    type="button"
                    @click="closeModal"
                    class="flex-1 py-3 border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-50 transition"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    :disabled="!canSubmit || submitting"
                    class="flex-1 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition disabled:opacity-50"
                  >
                    {{ submitting ? '저장 중...' : (editingProduct ? '수정하기' : '등록하기') }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { doc, updateDoc, deleteDoc } from 'firebase/firestore'
import { db, storage } from '@/firebase/firebase'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { usePoint } from '@/composables/main/usePoint'

export default {
  name: 'ProductManageView',
  components: {
    AdminLayout
  },
  setup() {
    const { getAllProducts, createProduct, updateProduct } = usePoint()

    const loading = ref(true)
    const products = ref([])
    const selectedCategory = ref('all')
    const showModal = ref(false)
    const editingProduct = ref(null)
    const submitting = ref(false)

    const categories = [
      { id: 'all', label: '전체', icon: '📦' },
      { id: 'coffee', label: '커피', icon: '☕' },
      { id: 'chicken', label: '치킨', icon: '🍗' },
      { id: 'pizza', label: '피자', icon: '🍕' },
      { id: 'burger', label: '버거', icon: '🍔' },
      { id: 'convenience', label: '편의점', icon: '🏪' },
      { id: 'icecream', label: '아이스크림', icon: '🍦' },
      { id: 'bakery', label: '베이커리', icon: '🥐' },
      { id: 'voucher', label: '상품권', icon: '🎫' },
      { id: 'etc', label: '기타', icon: '🎁' }
    ]

    const form = ref({
      category: '',
      brand: '',
      name: '',
      description: '',
      price: 0,
      stock: 10,
      order: 0,
      unlimitedStock: false
    })

    const selectedFile = ref(null)
    const previewUrl = ref('')

    const filteredProducts = computed(() => {
      if (selectedCategory.value === 'all') {
        return products.value
      }
      return products.value.filter(p => p.category === selectedCategory.value)
    })

    const canSubmit = computed(() => {
      return form.value.category && form.value.name && form.value.price > 0
    })

    const getCategoryLabel = (categoryId) => {
      const cat = categories.find(c => c.id === categoryId)
      return cat ? `${cat.icon} ${cat.label}` : categoryId
    }

    const loadProducts = async () => {
      loading.value = true
      try {
        products.value = await getAllProducts()
      } catch (err) {
        console.error('상품 로드 실패:', err)
      } finally {
        loading.value = false
      }
    }

    const openCreateModal = () => {
      editingProduct.value = null
      form.value = {
        category: '',
        brand: '',
        name: '',
        description: '',
        price: 0,
        stock: 10,
        order: products.value.length,
        unlimitedStock: false
      }
      selectedFile.value = null
      previewUrl.value = ''
      showModal.value = true
    }

    const openEditModal = (product) => {
      editingProduct.value = product
      form.value = {
        category: product.category || '',
        brand: product.brand || '',
        name: product.name || '',
        description: product.description || '',
        price: product.price || 0,
        stock: product.stock || 0,
        order: product.order || 0,
        unlimitedStock: product.stock < 0
      }
      selectedFile.value = null
      previewUrl.value = product.imageUrl || ''
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      editingProduct.value = null
    }

    const handleFileSelect = (e) => {
      const file = e.target.files[0]
      if (file) {
        selectedFile.value = file
        previewUrl.value = URL.createObjectURL(file)
      }
    }

    const handleDrop = (e) => {
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        selectedFile.value = file
        previewUrl.value = URL.createObjectURL(file)
      }
    }

    const clearImage = () => {
      selectedFile.value = null
      previewUrl.value = editingProduct.value?.imageUrl || ''
    }

    const uploadImage = async (file) => {
      const fileName = `products/${Date.now()}_${file.name}`
      const fileRef = storageRef(storage, fileName)
      await uploadBytes(fileRef, file)
      return getDownloadURL(fileRef)
    }

    const saveProduct = async () => {
      if (!canSubmit.value) return

      submitting.value = true
      try {
        let imageUrl = editingProduct.value?.imageUrl || ''

        if (selectedFile.value) {
          imageUrl = await uploadImage(selectedFile.value)
        }

        const productData = {
          category: form.value.category,
          brand: form.value.brand.trim(),
          name: form.value.name.trim(),
          description: form.value.description.trim(),
          price: form.value.price,
          stock: form.value.unlimitedStock ? -1 : form.value.stock,
          order: form.value.order,
          imageUrl
        }

        if (editingProduct.value) {
          await updateProduct(editingProduct.value.id, productData)
          alert('상품이 수정되었습니다.')
        } else {
          await createProduct(productData)
          alert('상품이 등록되었습니다.')
        }

        closeModal()
        await loadProducts()
      } catch (err) {
        console.error('상품 저장 실패:', err)
        alert('저장에 실패했습니다: ' + err.message)
      } finally {
        submitting.value = false
      }
    }

    const toggleActive = async (product) => {
      try {
        await updateDoc(doc(db, 'products', product.id), {
          isActive: !product.isActive,
          updatedAt: new Date().toISOString()
        })
        await loadProducts()
      } catch (err) {
        console.error('상태 변경 실패:', err)
        alert('상태 변경에 실패했습니다.')
      }
    }

    const deleteProduct = async (product) => {
      if (!confirm(`"${product.name}" 상품을 삭제하시겠습니까?`)) return

      try {
        await deleteDoc(doc(db, 'products', product.id))
        await loadProducts()
        alert('상품이 삭제되었습니다.')
      } catch (err) {
        console.error('삭제 실패:', err)
        alert('삭제에 실패했습니다.')
      }
    }

    onMounted(loadProducts)

    return {
      loading,
      products,
      categories,
      selectedCategory,
      filteredProducts,
      showModal,
      editingProduct,
      submitting,
      form,
      previewUrl,
      canSubmit,
      getCategoryLabel,
      openCreateModal,
      openEditModal,
      closeModal,
      handleFileSelect,
      handleDrop,
      clearImage,
      saveProduct,
      toggleActive,
      deleteProduct
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
